<template>
  <div
    class="table-container"
    :style="generateCustomStyle">
    <div
      v-if="!!slots.firstActionLine"
      class="table-action first-line">
      <div class="slot-container">
        <slot name="firstActionLine" />
      </div>
      <div
        v-if="!hideScrollBtn"
        class="scroll-control-btn">
        <span
          class="left"
          @mousedown.prevent="() => holdScroll('left')"
          @click.prevent="(e: MouseEvent) => handleScrollBnt('left', e)">
          <BaseSvg
            :src="ChevronLeft"
            width="16px"
            height="16px" />
        </span>
        <span class="split" />
        <span
          class="right"
          @mousedown="() => holdScroll('right')"
          @click.prevent="(e) => handleScrollBnt('right', e)">
          <BaseSvg
            :src="ChevronRight"
            width="16px"
            height="16px" />
        </span>
      </div>
    </div>
    <div
      v-if="!!slots.secondActionLine"
      class="table-action second-line">
      <slot name="secondActionLine" />
    </div>
    <div class="wrapper-container">
      <div
        v-if="checkWrapperScrollShadow('left')"
        class="box-shadow box-shadow-left"/>
      <div
        v-if="checkWrapperScrollShadow('right')"
        class="box-shadow box-shadow-right" />
      <div
        ref="refWrapper"
        class="table-wrapper"
        :class="{ 'auto-width': columnAutoWidth }"
        @scroll="handleScroll">
        <div
          v-if="data?.length && !loading"
          class="table-header">
          <div class="table-row">
            <div
              v-for="(column, index) in columns"
              :key="index"
              class="table-cell"
              :class="[
                column.key,
                !!column.fixed && `fixed fixed-${column.fixed}`,
                { 'on-hover': column.onHover }
              ]"
              :style="generateHeaderStyle(column)">
              <span
                v-if="selectable && index === 0"
                class="select-box select-box-header"
                :class="allSelectBoxClass"
                @click="handleSelectAll">
                <BaseSvg
                  class="check-icon"
                  width="16px"
                  :src="CheckIcon" />
              </span>
              <div class="content">
                <slot
                  v-if="slots['column-' + column.key]"
                  :name="'column-' + column.key"
                  :columnData="column" />
                <div v-else>
                  <div>
                    {{ column.title }}
                  </div>
                  <BaseSvg
                    v-if="column.sort"
                    class="sort-icon"
                    :src="SortIcon"
                    @click="emit('sort', column.key)" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="data?.length && !loading"
          class="table-body">
          <div
            v-for="(item, index) in data"
            :id="`row-${item.id}`"
            :key="`${index}-item`"
            class="table-row-container">
            <div
              :id="`row-${index}`"
              class="table-row"
              :class="{ disabled: item.disabled, 'row-click-enabled': rowClickEnabled }"
              @click="rowClickHandler(item)">
              <div
                v-for="(column, columnIndex) in columns"
                :key="columnIndex"
                class="table-cell"
                :style="generateBodyCellStyle(column)"
                :class="[
                  column.key,
                  !!column.fixed && `fixed fixed-${column.fixed}`,
                  { 'on-hover': column.onHover }
                ]">
                <div
                  v-if="selectable && columnIndex === 0"
                  :key="item.selected"
                  class="select-box"
                  :class="{ selected: item.selected, 'select-disabled': item.disabled }"
                  @click="selectRow(item)">
                  <BaseSvg
                    v-if="item.selected"
                    :src="CheckIcon"
                    width="16px" />
                </div>
                <span
                  v-if="
                    tableExpandable && columnIndex === columns.length - 1 && !item.hiddenExpandable
                  "
                  class="expand-icon"
                  :class="{ expanded: item.expanded, 'expand-disabled': item.disabled }"
                  @click="handleExpand(item)">
                  <BaseSvg
                    :src="ExpandIcon"
                    alt="expand-icon" />
                </span>
                <span
                  v-if="rightOptions && columnIndex === columns.length - 1 && !item.hiddenAction"
                  class="right-options">
                  <BaseDropDown
                    :options="generateRightOptions({ hideAction: item?.hideAction || [] })"
                    :selectable="false"
                    :disabled="
                      generateRightOptions({ hideAction: item?.hideAction || [] })?.length === 0
                    "
                    position="left"
                    @change="(option: any) => option.handler(item)">
                    <BaseSvg :src="OptionIcon" />
                  </BaseDropDown>
                </span>
                <div class="content">
                  <slot
                    :name="column.key"
                    :rowData="item">
                    <div class="text-content">
                      {{ item[column.key] }}
                    </div>
                    <BaseSvg
                      v-if="column.copy"
                      v-tooltip="{ value: 'Kopyala', position: 'top' }"
                      :src="CopyIcon"
                      width="16px"
                      height="16px"
                      customClass="copy-icon"
                      @click.stop="copyValue(item[column.key])" />
                  </slot>
                </div>
              </div>
            </div>
            <div
              v-if="item.expanded"
              class="table-collapse table-detail"
              :class="{ expanded: item.expanded }">
              <slot
                name="table-detail"
                :rowData="item" />
            </div>
          </div>
        </div>
        <div
          v-if="loading"
          class="loader-wrapper">
          <span
            v-if="loading"
            class="loader" />
        </div>
        <div
          v-if="!data?.length && !loading"
          class="no-data-container">
          <span
            v-if="!slots.noData"
            v-html="noDataText" />
          <slot
            v-else
            name="noData" />
        </div>
      </div>
    </div>
    <div
      v-if="!!slots.tableFooter"
      class="table-footer">
      <slot name="tableFooter" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  useSlots,
  computed,
  ref,
  onMounted,
  nextTick,
  watch,
  onUnmounted,
  useTemplateRef
} from 'vue';
import { defaultCustomStyles } from './resources/BaseTable/resources';
import BaseSvg from './BaseSvg.vue';
import BaseDropDown from './BaseDropDown.vue';
import {
  CheckIcon,
  ChevronLeft,
  ChevronRight,
  ExpandIcon,
  OptionIcon,
  SortIcon,
  CopyIcon
} from '@/assets/icons';
import toast from '@/utils/toast';

export interface IColumns {
  title?: string;
  width?: number | string;
  key: string;
  fixed?: string;
  sort?: boolean;
  copy?: boolean;
  onHover?: boolean;
  justify?: 'center' | 'flex-start' | 'flex-end' | 'start' | 'end' | 'stretch';
  align?: 'center' | 'flex-start' | 'flex-end' | 'start' | 'end' | 'stretch';
  bodyAlign?: 'center' | 'flex-start' | 'flex-end' | 'start' | 'end' | 'stretch';
  bodyJustify?: 'center' | 'flex-start' | 'flex-end' | 'start' | 'end' | 'stretch';
}

export interface IRightOptions {
  key: string;
  value: string;
  label: string;
  handler?: () => void;
}

export interface ITable {
  columns: IColumns[];
  data: any[];
  customStyles?: any;
  columnAutoWidth?: boolean;
  selectable?: boolean;
  allSelectedDisabled?: boolean;
  noDataText?: string;
  tableExpandable?: boolean;
  multipleExpandable?: boolean;
  rightOptions?: IRightOptions[];
  loading?: boolean;
  rowClickEnabled?: boolean;
  hideScrollBtn?: boolean;
}
const props = withDefaults(defineProps<ITable>(), {
  customStyles: defaultCustomStyles,
  columnAutoWidth: true,
  selectable: true,
  allSelectedDisabled: false,
  noDataText: 'Kayıt bulunamadı.',
  tableExpandable: false,
  multipleExpandable: false,
  hideScrollBtn: false,
  rowClickEnabled: false
});

const emit = defineEmits(['selectItem', 'selectAll', 'sort', 'handleExpand', 'rowClick']);

const slots = useSlots();

onMounted(() => {
  nextTick(() => {
    handleScroll();
    checkWrapperScrollShadow('right');
  });
});

const refWrapper = useTemplateRef<HTMLDivElement | null>('refWrapper');
const scrollShadowPosition = ref('');
const allSelected = ref<boolean>(false);

const generateCustomStyle = computed(() => {
  let widthString = '';
  if (props.columnAutoWidth) {
    (props.columns || []).forEach((column) => {
      if (typeof column.width === 'number') {
        widthString += ` ${column.width}px`;
      } else if (column.width === undefined) {
        widthString += ` ${0}px`;
      } else {
        widthString += ` ${column.width || 'auto'}`;
      }
    });
  }
  const allCustomStyles = { ...defaultCustomStyles, ...props.customStyles };
  const customVariables = Object.entries(allCustomStyles)
    .map(([key, value]) => `--${key}: ${value};`)
    .join('\n');
  return customVariables + `\n--gridColumnTemplate: ${widthString};`;
});

const allSelectBoxClass = computed(() => {
  const selectedLength = selectedData.value.length || 0;
  const selectableDataLength =
    (props.data || [])?.filter((item: any) => item.disabled !== true)?.length || 0;
  return {
    'half-selected':
      selectedLength > 0 && selectedLength !== selectableDataLength && !props.allSelectedDisabled,
    'all-selected':
      selectedLength === selectableDataLength &&
      selectableDataLength !== 0 &&
      !props.allSelectedDisabled,
    'select-disabled': props.allSelectedDisabled
  };
});

const selectedData = computed(() => {
  return (props.data || []).filter((val: any) => val.selected) || 0;
});

const generateHeaderStyle = (column: any) => {
  return {
    minWidth: column.width + 'px',
    maxWidth: column.width + 'px',
    justifyContent: column.justify,
    alignItems: column.align
  };
};

const generateBodyCellStyle = (column: any) => {
  return {
    minWidth: column.width + 'px',
    maxWidth: column.onHover ? 'unset' : column.width + 'px',
    justifyContent: column.bodyJustify,
    alignItems: column.bodyAlign
  };
};

const generateClasses = (element: any, className: string, action: 'add' | 'remove') => {
  element.forEach((element: any) => {
    element.classList[action](className);
  });
};

const handleScrollBnt = (direction: string, event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const tagName = target.tagName.toLowerCase();
  const classList = ['img', 'svg'].includes(tagName)
    ? target?.parentElement?.classList
    : target?.classList;
  const element: any = refWrapper.value;
  if (Object.values(classList || {}).includes('disabled') || !element) {
    return;
  }
  if (direction === 'left') {
    element.scrollLeft -= 100;
  } else {
    element.scrollLeft += 100;
  }
};

const holdScroll = (direction: string) => {
  let element: any = refWrapper.value;
  element.scrollTo({
    left: direction === 'right' ? element.scrollWidth : 0,
    behavior: 'smooth'
  });
};

const handleScroll = () => {
  let element: any = refWrapper.value;
  if (element) {
    const leftFixed: any = element.querySelectorAll('.table-cell.fixed-left');
    const rightFixed: any = element.querySelectorAll('.table-cell.fixed-right');
    const scrollBtn: any = document.querySelector('.scroll-control-btn');
    const rightScrollBtn: any = document.querySelector('.scroll-control-btn .right');
    const leftScrollBtn: any = document.querySelector('.scroll-control-btn .left');

    const { scrollWidth, clientWidth, offsetWidth, scrollLeft } = element;

    if (scrollBtn && !props.hideScrollBtn && !!slots.firstActionLine) {
      scrollBtn.style.display = scrollWidth === clientWidth ? 'none' : 'flex';
    }

    if (scrollWidth === clientWidth) return;

    if (Math.ceil(scrollLeft + offsetWidth) === scrollWidth) {
      scrollShadowPosition.value = 'shadow-right';
      generateClasses(leftFixed, 'shadow-right', 'add');
      generateClasses(rightFixed, 'shadow-left', 'remove');
      if (!props.hideScrollBtn && !!slots.firstActionLine) {
        leftScrollBtn.classList.remove('disabled');
        rightScrollBtn.classList.add('disabled');
      }
    } else if (scrollLeft === 0) {
      scrollShadowPosition.value = 'shadow-left';
      generateClasses(leftFixed, 'shadow-right', 'remove');
      generateClasses(rightFixed, 'shadow-left', 'add');
      if (!props.hideScrollBtn && !!slots.firstActionLine) {
        leftScrollBtn.classList.add('disabled');
        rightScrollBtn.classList.remove('disabled');
      }
    } else {
      scrollShadowPosition.value = 'shadow';
      generateClasses(leftFixed, 'shadow-right', 'add');
      generateClasses(rightFixed, 'shadow-left', 'add');
      if (!props.hideScrollBtn && !!slots.firstActionLine) {
        leftScrollBtn.classList.remove('disabled');
        rightScrollBtn.classList.remove('disabled');
      }
    }
  }
};

const checkWrapperScrollShadow = (direction: string) => {
  const shadowPosition = direction === 'left' ? 'right' : 'left';
  return (
    !(props.columns || [])?.find((val) => val.fixed === direction) &&
    [`shadow-${shadowPosition}`, 'shadow'].includes(scrollShadowPosition.value)
  );
};

const copyValue = (value: string) => {
  navigator.clipboard.writeText(value);
  toast({ title: 'Panoya kopyalandı.', type: 'success' });
};
const selectRow = (item: any) => {
  if (item.disabled) return;
  item.selected = !item.selected;
  emit('selectItem', item.selected, item, selectedData.value);
};

const handleSelectAll = () => {
  if (props.allSelectedDisabled) return;
  // Durum veriden türetilir; sayfa veya arama değişince eski değer kalmasın.
  allSelected.value = !allSelectBoxClass.value['all-selected'];
  props.data?.forEach((val: any) => {
    if (!val.disabled) {
      val.selected = allSelected.value;
    }
  });
  emit('selectAll', allSelected.value, props.data, selectedData.value);
};

const currentDataRows = () => {
  const tableRow: any = refWrapper.value?.querySelectorAll('.table-row');
  if (!tableRow) return;
  tableRow.forEach((row: any) => {
    const haveOnHoverCell = row?.querySelector('.table-cell.on-hover');
    const tableRowHeight: any = row?.getBoundingClientRect()?.height;
    if (haveOnHoverCell) haveOnHoverCell.style.height = tableRowHeight + 'px';
  });
};

const calcHeight = (item: any, id: string) => {
  const clickedRow: any = refWrapper.value?.querySelector(`#${id}`);
  const tableRow: any = clickedRow?.querySelector('.table-row');
  const detailArea: any = clickedRow?.querySelector('.table-collapse.table-detail');
  const targetElement = detailArea?.firstElementChild;
  const targetHeight: any = targetElement?.getBoundingClientRect()?.height;
  const tableRowHeight: any = tableRow?.getBoundingClientRect()?.height;
  const paddingOffset: number = 20;

  const calculatedHeight = targetHeight + paddingOffset * 2 + tableRowHeight;
  if (clickedRow) {
    clickedRow!.style.minHeight = item.expanded ? calculatedHeight + 'px' : '0';
  }
  if (detailArea) {
    detailArea.style.minHeight = item.expanded ? targetHeight + paddingOffset * 2 + 'px' : '0';
  }
};

const rowClickHandler = (row: any) => {
  if (props.rowClickEnabled) {
    emit('rowClick', row);
  }
};

const handleExpand = (item: any) => {
  if (item.disabled) return;
  item.expanded = !item.expanded;

  if (!props.multipleExpandable) {
    props.data?.forEach((val: any) => {
      if (item.id !== val.id) {
        val.expanded = false;
      }
      nextTick(() => {
        calcHeight(val, `row-${val.id}`);
      });
    });
  } else {
    nextTick(() => {
      calcHeight(item, `row-${item.id}`);
    });
  }
  emit('handleExpand', item.expanded, item);
};
watch(
  () => props.data.length,
  () => {
    props.data?.forEach((val: any) => {
      nextTick(() => {
        calcHeight(val, `row-${val.id}`);
      });
    });
    nextTick(handleScroll);

    nextTick(currentDataRows);
  }
);

onMounted(() => {
  nextTick(currentDataRows);
});
onUnmounted(() => {
  props.data?.forEach((val: any) => {
    val.expanded = false;
  });
});

const generateRightOptions = ({ hideAction = [] }: { hideAction: any }) => {
  return props.rightOptions?.filter(
    (el: any) => !(hideAction || [])?.includes(el.key)
  ) as IRightOptions[];
};
</script>

<style lang="scss" scoped src="./styles/table.scss" />
